<script setup>
import { computed, shallowRef, watch } from 'vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { formatDateTime } from '@/utils/format';

// Who did what, and when: sign-ins, account changes, grades, attendance,
// discipline reports and settings. Read-only, and for the superadmin only
// (the server refuses everyone else).
useSessionGuard();
const toast = useToast();

const CATEGORIES = [
  { value: null, title: 'All activity' },
  { value: 'account', title: 'Accounts and sign-ins' },
  { value: 'students', title: 'Student records' },
  { value: 'grades', title: 'Grades' },
  { value: 'attendance', title: 'Attendance' },
  { value: 'discipline', title: 'Discipline reports' },
  { value: 'settings', title: 'Settings' },
];
const CATEGORY_BADGES = {
  account: { label: 'Account', variant: 'blue' },
  students: { label: 'Students', variant: 'amber' },
  grades: { label: 'Grades', variant: 'violet' },
  attendance: { label: 'Attendance', variant: 'green' },
  discipline: { label: 'Discipline', variant: 'red' },
  settings: { label: 'Settings', variant: 'secondary' },
};
// Failed sign-ins stand out.
const isWarning = (row) => row.action === 'auth.sign_in_failed';

const headers = [
  { title: 'When', key: 'createdAt', width: 180, sortable: false },
  { title: 'Who', key: 'actorName', width: 200, sortable: false },
  { title: 'Type', key: 'category', width: 120, sortable: false },
  { title: 'What happened', key: 'summary', sortable: false },
];

const category = shallowRef(null);
const from = shallowRef('');
const to = shallowRef('');
const search = shallowRef('');
const page = shallowRef(1);
const pageSize = shallowRef(25);

const items = shallowRef([]);
const total = shallowRef(0);
const loading = shallowRef(false);
const error = shallowRef('');
const denied = shallowRef(false);

const query = computed(() => {
  const params = new URLSearchParams();
  if (category.value) params.set('category', category.value);
  if (from.value) params.set('from', from.value);
  if (to.value) params.set('to', to.value);
  if (search.value?.trim()) params.set('q', search.value.trim());
  return params;
});

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams(query.value);
    params.set('page', page.value);
    params.set('pageSize', pageSize.value);
    const body = await apiRequest('get', `/activity-log?${params}`);
    items.value = body.items || [];
    total.value = body.total || 0;
  } catch (e) {
    if (e?.response?.status === 403) denied.value = true;
    else error.value = errorMessage(e, 'Could not load the activity log.');
  } finally {
    loading.value = false;
  }
}

// Filters start again from page 1; typing in the search waits a moment.
let searchTimer = null;
watch([category, from, to], () => {
  page.value = 1;
  load();
});
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    load();
  }, 350);
});
watch([page, pageSize], load, { immediate: true });

function onOptions({ page: p, itemsPerPage }) {
  page.value = p;
  pageSize.value = itemsPerPage;
}

const exporting = shallowRef(false);
async function exportCsv() {
  exporting.value = true;
  try {
    const blob = await apiRequest('get', `/activity-log/export?${query.value}`, undefined, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'activity-log.csv' }).click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errorMessage(e, 'Could not export the activity log.'));
  } finally {
    exporting.value = false;
  }
}

// Details of one entry (old and new values, IP address).
const selected = shallowRef(null);
const detailsOpen = shallowRef(false);
const details = computed(() => {
  try {
    return selected.value?.details ? JSON.parse(selected.value.details) : null;
  } catch {
    return null;
  }
});
const changes = computed(() => (Array.isArray(details.value?.changed) ? details.value.changed : []));
function openRow(_, { item }) {
  selected.value = item;
  detailsOpen.value = true;
}
const rowProps = ({ item }) => ({ class: ['log__row', { 'log__row--warning': isWarning(item) }] });
</script>

<template>
  <UiCard
    title="Activity log"
    description="Who did what, and when: sign-ins, accounts, grades, attendance, discipline reports and settings. Entries can't be edited and are kept for one year."
    flush
  >
    <template #toolbar>
      <v-select
        v-model="category"
        :items="CATEGORIES"
        density="compact"
        hide-details
        aria-label="Type of activity"
        class="log__filter"
      />
      <v-text-field
        v-model="from"
        type="date"
        density="compact"
        hide-details
        label="From"
        class="log__date"
      />
      <v-text-field
        v-model="to"
        type="date"
        density="compact"
        hide-details
        label="To"
        class="log__date"
      />
      <div class="ui-card__toolbar-end">
        <v-text-field
          v-model="search"
          placeholder="Search name or activity"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
          class="card-search"
        />
        <v-btn variant="outlined" prepend-icon="mdi-download" :loading="exporting" :disabled="denied" @click="exportCsv">
          Export CSV
        </v-btn>
      </div>
    </template>

    <div v-if="denied" class="log__state">
      <UiEmptyState
        icon="mdi-lock-outline"
        title="No access"
        description="Only the superadmin can view the activity log."
      />
    </div>

    <template v-else>
      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-6 mb-4">{{ error }}</v-alert>

      <v-data-table-server
        :headers="headers"
        :items="items"
        :items-length="total"
        :loading="loading"
        :page="page"
        :items-per-page="pageSize"
        :items-per-page-options="[25, 50, 100]"
        :row-props="rowProps"
        class="table-flush"
        @update:options="onOptions"
        @click:row="openRow"
      >
        <template #[`item.createdAt`]="{ item }">
          <span class="log__when">{{ formatDateTime(item.createdAt) }}</span>
        </template>
        <template #[`item.actorName`]="{ item }">
          {{ item.actorName || 'System' }}
        </template>
        <template #[`item.category`]="{ item }">
          <UiBadge :variant="CATEGORY_BADGES[item.category]?.variant || 'secondary'">
            {{ CATEGORY_BADGES[item.category]?.label || item.category }}
          </UiBadge>
        </template>
        <template #[`item.summary`]="{ item }">
          <span class="log__summary">
            <v-icon v-if="isWarning(item)" size="16" color="warning">mdi-alert-outline</v-icon>
            {{ item.summary }}
          </span>
        </template>
        <template #no-data>
          <div class="table-empty">No activity matches these filters.</div>
        </template>
      </v-data-table-server>
    </template>
  </UiCard>

  <UiDialog
    v-model="detailsOpen"
    title="Activity details"
    :description="selected ? formatDateTime(selected.createdAt) : ''"
    :persistent="false"
  >
    <dl v-if="selected" class="log__details">
      <dt>Who</dt>
      <dd>{{ selected.actorName || 'System' }}</dd>
      <dt>What happened</dt>
      <dd>{{ selected.summary }}</dd>
      <template v-if="selected.level">
        <dt>Level</dt>
        <dd>{{ selected.level }}</dd>
      </template>
      <template v-if="selected.ip">
        <dt>IP address</dt>
        <dd>{{ selected.ip }}</dd>
      </template>
    </dl>
    <template v-if="changes.length">
      <p class="log__changes-title">Changes</p>
      <ul class="log__changes">
        <li v-for="(c, i) in changes" :key="i">
          <span>{{ c.title || (c.studentId ? `Student #${c.studentId}` : 'Value') }}</span>
          <span class="log__change">{{ c.before ?? '—' }} → {{ c.after ?? '—' }}</span>
        </li>
      </ul>
    </template>
    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.log__filter {
  width: 220px;
  flex: 0 0 auto;
}
.log__date {
  width: 160px;
  flex: 0 0 auto;
}
.log__state {
  padding: 24px;
}
.log__when {
  white-space: nowrap;
  color: var(--muted-foreground);
}
.log__summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
:deep(.log__row) {
  cursor: pointer;
}
:deep(.log__row--warning) {
  background-color: rgba(var(--v-theme-warning), 0.06);
}
.log__details {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 16px;
  margin: 0;
  font-size: 14px;
}
.log__details dt {
  color: var(--muted-foreground);
}
.log__details dd {
  margin: 0;
}
.log__changes-title {
  margin: 20px 0 8px;
  font-size: 14px;
  font-weight: 600;
}
.log__changes {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
}
.log__changes li {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}
.log__change {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
@media (max-width: 600px) {
  .log__filter,
  .log__date {
    width: 100%;
  }
}
</style>
